import React, { useState, useEffect, useMemo } from "react";
import { Trophy } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import api from "@/lib/api";

/* -------------------------------------------------------------------------- *
 * Season Stats leaderboard — three columns (runs / wickets / catches),
 * top 5 each, with a grade selector. Auto-shows the previous season if
 * the current one has no match data yet. Photos come from the matching
 * user's ``avatar_url`` when we can pair the stat line to a member.
 * -------------------------------------------------------------------------- */
export default function StatsBoard({ align = "center" }) {
  const [data, setData] = useState(null);
  const [ladder, setLadder] = useState(null);
  const [grade, setGrade] = useState("");   // "" = All grades
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = grade ? { grade } : {};
        const [statsRes, ladderRes] = await Promise.all([
          api.get("/public/landing/stats", { params }),
          api.get("/public/landing/ladder").catch(() => ({ data: null })),
        ]);
        if (alive) {
          setData(statsRes.data || null);
          setLadder(ladderRes.data || null);
        }
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [grade]);

  if (!loading && !data?.season) return null;

  const columns = [
    { key: "runs", title: "Most Runs", rows: data?.runs || [] },
    { key: "wickets", title: "Most Wickets", rows: data?.wickets || [] },
    { key: "catches", title: "Most Catches", rows: data?.catches || [] },
    { key: "batting_avg", title: "Batting Average", rows: data?.batting_avg || [] },
    { key: "bowling_avg", title: "Bowling Average", rows: data?.bowling_avg || [] },
    { key: "hundreds", title: "100's", rows: data?.hundreds || [] },
    { key: "fifties", title: "50's", rows: data?.fifties || [] },
    { key: "five_wickets", title: "5 Wickets", rows: data?.five_wickets || [] },
    { key: "ducks", title: "Ducks", rows: data?.ducks || [] },
  ];

  return (
    <section className="border-b border-border bg-[hsl(var(--secondary))]/30" data-testid="stats-board">
      <div className={`px-5 sm:px-6 lg:px-8 pt-0 pb-4 sm:pb-6${align === "center" ? " mx-auto" : ""}`} style={{ maxWidth: 1920 }}>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-3 py-3 pl-5 sm:pl-6 lg:pl-[50px] pr-5 sm:pr-6 lg:pr-[232px] -mx-5 sm:-mx-6 lg:ml-[150px] lg:-mr-8 sticky top-16 z-10" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
          <div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white"
              style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
            >
              Stats
            </h2>
            {data?.season && (
              <div className="mt-1 text-xs uppercase tracking-[0.14em] text-white/70 font-semibold">
                {data.season}
              </div>
            )}
          </div>

          {(data?.grades || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="stats-board-grade">
              <button
                onClick={() => setGrade("")}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                  grade === ""
                    ? "bg-white text-[#1E3A8A] border-transparent"
                    : "bg-white/20 text-white border-white/30 hover:bg-white/30"
                }`}
              >
                All
              </button>
              <select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="h-8 px-3 rounded-full text-xs font-semibold border border-white/30 bg-white/20 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              >
                <option value="" className="text-zinc-900 bg-white">Choose grade…</option>
                {data.grades.map((g) => <option key={g} value={g} className="text-zinc-900 bg-white">{g}</option>)}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-sm text-muted-foreground py-8">Loading stats…</div>
        ) : (
          <>
            {/* Horizontal scroll rail — aligned with banner left edge and grade button right edge */}
            <div
              className="-mx-5 sm:-mx-6 lg:ml-[150px] lg:mr-[200px] pl-5 sm:pl-6 lg:pl-[50px] pr-3 sm:pr-4 lg:pr-4 overflow-x-auto snap-x snap-mandatory scrollbar-none"
              style={{ WebkitOverflowScrolling: "touch" }}
              data-testid="stats-board-swipe"
            >
              <div className="flex gap-4 sm:gap-6 pb-2">
                {/* Ladder card — first position, only when data exists */}
                {(ladder?.grades || []).length > 0 && (
                  <div className="snap-center shrink-0 w-[75%] sm:w-[45%] md:w-[calc((100%-4.5rem)/4)]">
                    <LadderCard grades={ladder.grades} season={ladder?.season} isFallback={ladder?.is_fallback} />
                  </div>
                )}
                {columns.filter((col) => col.rows.length > 0).map((col) => (
                  <div key={col.key} className="snap-center shrink-0 w-[75%] sm:w-[45%] md:w-[calc((100%-4.5rem)/4)]">
                    <StatsColumn title={col.title} rows={col.rows} />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function StatsColumn({ title, rows }) {
  const first = rows[0];
  const rest = rows.slice(1);
  return (
    <div className="rounded-2xl overflow-hidden bg-background border border-border" data-testid={`stats-col-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="px-4 sm:px-5 py-2.5 border-b border-border">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">{title}</div>
      </div>
      {first ? (
        <div
          className="relative px-4 sm:px-5 py-3 overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2749c9 55%, #4169E1 100%)" }}
        >
          <div className="relative z-10 pr-28 sm:pr-32 md:pr-36">
            <div className="flex items-center gap-1.5">
              <div className="text-white text-base sm:text-lg font-bold leading-tight">{(() => { const parts = (first.name || "").split(" "); const last = parts.pop(); return <>{parts.join(" ")}<br />{last}</>; })()}</div>
              {first.is_record && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Trophy className="h-4 w-4 text-amber-300 shrink-0 cursor-help" data-testid={`stats-record-trophy-${title.replace(/\s+/g, "-").toLowerCase()}-1`} />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs max-w-[200px]">
                      <p className="font-semibold">All-Time Club Record</p>
                      <p className="text-muted-foreground">{first.record_holder}: {first.record_value}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div
              className="mt-1 text-white text-5xl sm:text-6xl font-black leading-none tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
            >
              {first.value}
            </div>
          </div>
          <div className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 flex items-center">
            {first.avatar_url ? (
              <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-full overflow-hidden ring-4 ring-white/90 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)] bg-white">
                <img
                  src={first.avatar_url}
                  alt={first.name}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            ) : (
              <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-full ring-4 ring-white/70 bg-white/15" aria-hidden="true" />
            )}
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-xs text-muted-foreground">No data yet</div>
      )}
      {rest.length > 0 && (
        <ul className="divide-y divide-border">
          {rest.map((r, i) => (
            <li
              key={`${r.name}-${i}`}
              className="flex items-center gap-3 px-4 sm:px-5 py-2.5 bg-[#faf8f5] hover:bg-[#f5f0e8] transition-colors"
              data-testid={`stats-row-${i + 2}`}
            >
              <span className="text-xs sm:text-sm text-muted-foreground tabular-nums w-4">{i + 2}</span>
              {r.avatar_url ? (
                <img src={r.avatar_url} alt={r.name} className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-[hsl(var(--secondary))] border border-border" aria-hidden="true" />
              )}
              <span className="flex-1 text-sm font-semibold leading-tight">{(() => { const parts = (r.name || "").split(" "); const last = parts.pop(); return <>{parts.join(" ")}<br />{last}</>; })()}</span>
              {r.is_record && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Trophy className="h-3.5 w-3.5 text-amber-500 shrink-0 cursor-help" data-testid={`stats-record-trophy-row-${i + 2}`} />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs max-w-[200px]">
                      <p className="font-semibold">All-Time Club Record</p>
                      <p className="text-muted-foreground">{r.record_holder}: {r.record_value}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <span
                className="text-lg sm:text-xl font-black tabular-nums"
                style={{ color: "#4169E1", fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
              >
                {r.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- *
 * Ladder card — shows the current season's competition standings.
 * If multiple grades have ladder data, shows the first one with a
 * grade-switcher. Derwent teams are highlighted.
 * -------------------------------------------------------------------------- */
function LadderCard({ grades, season, isFallback }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = grades[activeIdx] || null;
  const teams = active?.teams || [];
  const hasData = teams.length > 0;
  const first = teams[0];
  const rest = teams.slice(1);

  return (
    <div className="rounded-2xl overflow-hidden bg-background border border-border flex flex-col" data-testid="stats-col-ladder">
      <div className="px-4 sm:px-5 py-2.5 border-b border-border">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">
            Ladder
          </div>
          {grades.length > 1 && (
            <select
              value={activeIdx}
              onChange={(e) => setActiveIdx(Number(e.target.value))}
              className="text-[10px] font-semibold bg-transparent border border-border rounded-md px-1.5 py-0.5 text-muted-foreground focus:outline-none"
              data-testid="ladder-grade-select"
            >
              {grades.map((g, i) => (
                <option key={g.grade_id} value={i}>{g.grade_name}</option>
              ))}
            </select>
          )}
        </div>
        {season && (
          <div className="text-[9px] text-muted-foreground/60 mt-0.5">
            {season}{isFallback ? " (Last Season)" : ""}
          </div>
        )}
      </div>

      {!hasData ? (
        <div className="flex-1 flex items-center justify-center py-10 px-4 text-center">
          <div>
            <div className="text-sm font-semibold text-muted-foreground/60">No data yet</div>
            <div className="text-[10px] text-muted-foreground/40 mt-1">Ladder standings will appear once the season begins</div>
          </div>
        </div>
      ) : (
        <>
          {/* Hero row — #1 team, matches the blue gradient of stats cards */}
          {first && (
            <div
              className="relative px-4 sm:px-5 py-3 overflow-hidden"
              style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2749c9 55%, #4169E1 100%)" }}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-white/70 text-[10px] font-semibold tracking-wider uppercase">#1</div>
                  <div className="text-white text-base sm:text-lg font-bold leading-tight mt-0.5">{first.name}</div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-white/70 text-[10px]">P <span className="text-white font-bold text-xs">{first.played}</span></span>
                    <span className="text-white/70 text-[10px]">W <span className="text-white font-bold text-xs">{first.won}</span></span>
                    <span className="text-white/70 text-[10px]">L <span className="text-white font-bold text-xs">{first.lost}</span></span>
                  </div>
                </div>
                <div
                  className="text-white text-5xl sm:text-6xl font-black leading-none tracking-tight"
                  style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
                >
                  {first.points}
                </div>
              </div>
            </div>
          )}
          {/* Remaining rows — scrollable to match stats card height */}
          {rest.length > 0 && (
            <ul className="divide-y divide-border overflow-y-auto" style={{ maxHeight: 240 }}>
              {rest.map((t, i) => (
                <li
                  key={t.team_id || `team-${i}`}
                  className={`flex items-center gap-3 px-4 sm:px-5 py-2.5 transition-colors ${t.is_club ? "bg-[#1E3A8A]/8 hover:bg-[#1E3A8A]/12" : "bg-[#faf8f5] hover:bg-[#f5f0e8]"}`}
                  data-testid={`ladder-row-${i + 1}`}
                >
                  <span className="text-xs sm:text-sm text-muted-foreground tabular-nums w-4">{t.position}</span>
                  <span className={`flex-1 text-sm leading-tight truncate ${t.is_club ? "text-[#1E3A8A] font-bold" : "font-semibold"}`}>{t.name}</span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">{t.played}-{t.won}-{t.lost}</span>
                  <span
                    className="text-lg sm:text-xl font-black tabular-nums"
                    style={{ color: "#4169E1", fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
                  >
                    {t.points}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {active && grades.length <= 1 && (
        <div className="px-4 py-2 border-t border-border/40">
          <div className="text-[9px] text-muted-foreground/50 truncate">{active.grade_name}</div>
        </div>
      )}
    </div>
  );
}
